import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { DataExportScheduleResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetActiveDataExportScheduleSubscription } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC data export schedule data to match the GraphQL query structure
 */
function mapTrpcDataExportScheduleToGraphQL(
  trpcData: DataExportScheduleResponseRow[] | undefined
): GetActiveDataExportScheduleSubscription | undefined {
  if (!trpcData) {
    return undefined;
  }

  return {
    data_export_schedule: trpcData,
  };
}

export const useGetActiveDataExportSchedule = (skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();

  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.dataExport.getActiveSchedule.queryOptions(),

    enabled: trpcEnabled && !skip,
  });

  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);
  const data = mapTrpcDataExportScheduleToGraphQL(trpcData);

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
    error: trpcError,
  };
};
